import { useCallback, useEffect, useState } from "react";

/** Below this width the side navigation starts folded, so the work area keeps room for a list beside its detail. */
export const SIDEBAR_FOLD_BELOW = 1280;

const narrowQuery = `(max-width: ${SIDEBAR_FOLD_BELOW - 0.02}px)`;
const isNarrow = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(narrowQuery).matches;

/**
 * Whether the side navigation is folded. On a wide screen the officer's choice is remembered under `storageKey`; on a narrower one the
 * navigation is folded by default and opening it lasts until the screen size changes, so a laptop never inherits a desktop preference.
 */
export function useSidebarCollapsed(storageKey: string): [collapsed: boolean, toggle: () => void] {
  const [narrow, setNarrow] = useState(isNarrow);
  const [openedWhileNarrow, setOpenedWhileNarrow] = useState(false);
  const [stored, setStored] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === "collapsed"; } catch { return false; }
  });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(narrowQuery);
    const onChange = () => { setNarrow(mq.matches); setOpenedWhileNarrow(false); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    if (narrow) { setOpenedWhileNarrow((v) => !v); return; }
    setStored((c) => {
      const next = !c;
      try { localStorage.setItem(storageKey, next ? "collapsed" : "expanded"); } catch { /* storage unavailable */ }
      return next;
    });
  }, [narrow, storageKey]);

  return [narrow ? !openedWhileNarrow : stored, toggle];
}
